/**
 * HISIP-005: Templates Catalog Hooks
 * 
 * Provides caching and access to system templates (personas, rubrics, styles)
 * Uses 5-minute in-memory cache to minimize network chatter as specified
 */

import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/App';

export interface TemplateReference {
  id: string;
  slug: string;
  name: string;
  description: string;
  version?: number;
  stage?: string;
  role?: string;
}

export interface GroupedTemplates {
  personas: TemplateReference[];
  rubrics: TemplateReference[];
  styles: TemplateReference[];
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  loading: boolean;
}

// 5-minute cache TTL as specified
const CACHE_TTL = 5 * 60 * 1000;

// In-memory cache store
const cache = {
  personas: null as CacheEntry<TemplateReference[]> | null,
  rubrics: null as CacheEntry<TemplateReference[]> | null,
  styles: null as CacheEntry<TemplateReference[]> | null,
  grouped: null as CacheEntry<GroupedTemplates> | null,
};

const isCacheValid = <T>(entry: CacheEntry<T> | null): boolean => {
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_TTL;
};

const createCacheEntry = <T>(data: T): CacheEntry<T> => ({
  data,
  timestamp: Date.now(),
  loading: false
});

/**
 * Hook for fetching persona templates with caching and optional filtering
 */
export const usePersonas = (filters?: { stage?: string; role?: string }) => {
  const [personas, setPersonas] = useState<TemplateReference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPersonas = async () => {
    // Check cache first
    if (cache.personas && isCacheValid(cache.personas)) {
      setPersonas(cache.personas.data);
      return;
    }

    // Prevent duplicate requests
    if (cache.personas?.loading) return;

    // Mark as loading
    cache.personas = { data: [], timestamp: 0, loading: true };
    setLoading(true);
    setError(null);

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const queryParams = new URLSearchParams();
      if (filters?.stage) queryParams.append('stage', filters.stage);
      if (filters?.role) queryParams.append('role', filters.role);
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/templates/personas${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch personas: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch personas');
      }

      const data = result.data || [];
      cache.personas = createCacheEntry(data);
      setPersonas(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching personas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [filters?.stage, filters?.role]);

  return { personas, loading, error, refetch: fetchPersonas };
};

/**
 * Hook for fetching rubric templates with caching
 */
export const useRubrics = () => {
  const [rubrics, setRubrics] = useState<TemplateReference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchRubrics = async () => {
    if (cache.rubrics && isCacheValid(cache.rubrics)) {
      setRubrics(cache.rubrics.data);
      return;
    }

    if (cache.rubrics?.loading) return;

    cache.rubrics = { data: [], timestamp: 0, loading: true };
    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/rubrics`, {
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch rubrics: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch rubrics');
      }

      const data = result.data || [];
      cache.rubrics = createCacheEntry(data);
      setRubrics(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching rubrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRubrics();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { rubrics, loading, error, refetch: fetchRubrics };
};

/**
 * Hook for fetching style templates with caching
 */
export const useStyles = () => {
  const [styles, setStyles] = useState<TemplateReference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchStyles = async () => {
    if (cache.styles && isCacheValid(cache.styles)) {
      setStyles(cache.styles.data);
      return;
    }

    if (cache.styles?.loading) return;

    cache.styles = { data: [], timestamp: 0, loading: true };
    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/styles`, {
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch styles: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch styles');
      }

      const data = result.data || [];
      cache.styles = createCacheEntry(data);
      setStyles(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching styles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStyles();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { styles, loading, error, refetch: fetchStyles };
};

/**
 * Hook for fetching all templates grouped by type
 * Most efficient for initial loads and dropdowns
 */
export const useGroupedTemplates = () => {
  const [templates, setTemplates] = useState<GroupedTemplates>({
    personas: [],
    rubrics: [],
    styles: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchGroupedTemplates = async () => {
    if (cache.grouped && isCacheValid(cache.grouped)) {
      setTemplates(cache.grouped.data);
      return;
    }

    if (cache.grouped?.loading) return;

    cache.grouped = { 
      data: { personas: [], rubrics: [], styles: [] }, 
      timestamp: 0, 
      loading: true 
    };
    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/grouped`, {
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch templates');
      }

      const data = result.data || { personas: [], rubrics: [], styles: [] };
      cache.grouped = createCacheEntry(data);
      
      // Also populate individual caches to avoid redundant requests
      cache.personas = createCacheEntry(data.personas);
      cache.rubrics = createCacheEntry(data.rubrics);  
      cache.styles = createCacheEntry(data.styles);
      
      setTemplates(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching grouped templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupedTemplates();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { templates, loading, error, refetch: fetchGroupedTemplates };
};

/**
 * Utility to invalidate all template caches
 * Useful after admin operations or when cache needs refresh
 */
export const invalidateTemplateCache = () => {
  cache.personas = null;
  cache.rubrics = null;
  cache.styles = null;
  cache.grouped = null;
};

/**
 * Check if style is applicable for a given stage
 * Based on the style-applicable stages: vision, storyboard, shots, continuity, frames, video
 */
export const isStyleApplicableStage = (stage: string): boolean => {
  const styleApplicableStages = ['vision', 'storyboard', 'shots', 'continuity', 'frames', 'video'];
  return styleApplicableStages.includes(stage);
};